import { useCallback, useEffect, useState } from "react";
import { useFormStore } from "../stores/formStore";

/** Délai avant d'afficher l'overlay de transition. Sous ce seuil, la sauvegarde
 * est imperceptible et un spinner qui clignote gênerait plus qu'il n'aiderait. */
const DELAI_OVERLAY_MS = 250;

interface UseFormPageNavigationOptions<TData> {
  /** Nombre de pages navigables : les index valides vont de 0 à pageCount - 1. */
  pageCount: number;
  /** `donnees.pageActuelle` de l'évaluation. Clampé : le découpage en pages peut
   * avoir changé entre deux sessions du même candidat. */
  pageActuelle?: number;
  /** Sauvegarde des réponses avant changement de page. Reçoit la page **cible**,
   * pour qu'une reprise de session rouvre la page réellement affichée. À l'appelant
   * de ne rien faire si l'évaluation n'est pas éditable. */
  save: (data: TData, page: number) => Promise<void>;
  /** Appelé juste avant le changement de page (animations de transition). */
  onPageChange?: (info: {
    from: number;
    to: number;
    direction: "next" | "prev";
  }) => void;
  /** Remonte en haut de page après navigation (défaut : true) — sinon la nouvelle
   * page arrive scrollée à la position de la précédente. */
  scrollToTop?: boolean;
}

/**
 * Navigation paginée partagée par les templates de formulaire : reprise de la page
 * persistée, sauvegarde avant transition, et verrou anti-clic-rapide.
 *
 * La sauvegarde est un aller-retour réseau pendant lequel l'ancienne page reste
 * affichée. Sans verrou, un clic rapide modifie une réponse de la page qu'on quitte
 * (silencieusement) ou lance une seconde sauvegarde concurrente. `isNavigating` est
 * un état (et non une ref) pour que l'appelant puisse neutraliser visuellement la
 * zone des questions et désactiver les boutons pendant la transition.
 */
export function useFormPageNavigation<TData>({
  pageCount,
  pageActuelle,
  save,
  onPageChange,
  scrollToTop = true,
}: UseFormPageNavigationOptions<TData>) {
  const { currentStep, setCurrentStep, setStartTime, reset } = useFormStore();
  const [isNavigating, setIsNavigating] = useState(false);
  const [showTransitionOverlay, setShowTransitionOverlay] = useState(false);

  const pageInitiale = Math.min(
    Math.max(pageActuelle ?? 0, 0),
    Math.max(pageCount - 1, 0),
  );

  useEffect(() => {
    reset();
    setCurrentStep(pageInitiale);
    setStartTime(Date.now());
    return () => {
      reset();
    };
    // pageInitiale volontairement hors deps : lecture au montage uniquement,
    // sinon la mise à jour réactive de l'évaluation par Convex après chaque
    // sauvegarde re-piloterait la page et annulerait la navigation en cours.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reset, setCurrentStep, setStartTime]);

  const goToPage = useCallback(
    async (page: number, data: TData) => {
      if (isNavigating || page < 0 || page >= pageCount || page === currentStep)
        return;

      setIsNavigating(true);
      const overlayTimer = setTimeout(
        () => setShowTransitionOverlay(true),
        DELAI_OVERLAY_MS,
      );
      try {
        await save(data, page);
        onPageChange?.({
          from: currentStep,
          to: page,
          direction: page > currentStep ? "next" : "prev",
        });
        setCurrentStep(page);
        if (scrollToTop) window.scrollTo({ top: 0 });
      } finally {
        clearTimeout(overlayTimer);
        setShowTransitionOverlay(false);
        setIsNavigating(false);
      }
    },
    [
      currentStep,
      isNavigating,
      onPageChange,
      pageCount,
      save,
      scrollToTop,
      setCurrentStep,
    ],
  );

  return {
    currentPage: currentStep,
    isNavigating,
    showTransitionOverlay,
    goToPage,
  };
}
